"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { DownloadAppLink } from "@/components/ui/DownloadAppLink";

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.2, 0, 0, 1] },
    },
  };
}

const avatars = [
  { name: "Rashid", src: "https://randomuser.me/api/portraits/men/32.jpg" },
  { name: "Omar", src: "https://randomuser.me/api/portraits/men/75.jpg" },
  { name: "Arjun", src: "https://randomuser.me/api/portraits/men/46.jpg" },
  { name: "Youssef", src: "https://randomuser.me/api/portraits/men/41.jpg" },
  { name: "Mohammed", src: "https://randomuser.me/api/portraits/men/52.jpg" },
];

// ── Section ─────────────────────────────────────────────────────────────────

export function CTASection() {
  const reduce = useReducedMotion() ?? false;

  return (
    <section
      className="bg-white px-5 pb-10 pt-16 sm:px-8 sm:pb-12 sm:pt-16 lg:pb-14 lg:pt-16"
      aria-labelledby="cta-heading"
    >
      {/* Positioning context for the card + the phone poking above it — not
          overflow-hidden, so the phone isn't clipped by the card's corners. */}
      <div className="relative mx-auto max-w-[1200px]">
        {/* ── Phone — poking above the card on sm+, pinned flush to its bottom
            edge (top & bottom both set, so height always matches the card
            exactly, no matter how tall its text content is). Hidden on mobile,
            where the phone instead sits at the end of the stacked content. */}
        <div className="pointer-events-none absolute right-12 top-[-32px] bottom-0 z-20 hidden w-[180px] sm:block lg:right-20 lg:top-[-42px] lg:w-[240px]">
          <div className="relative h-full w-full overflow-hidden rounded-t-[22px]">
            <Image
              src="/bento-phone.png"
              alt="JustGold app home screen showing a gold balance, buy and sell actions, and a price chart"
              fill
              sizes="(max-width: 1024px) 190px, 240px"
              className="object-cover object-top drop-shadow-[0_20px_40px_rgba(0,0,0,0.35)]"
              priority
            />
          </div>
        </div>

        <motion.div
          variants={container}
          initial={false}
          whileInView="visible"
          viewport={{ once: true, amount: 0.12 }}
          className="relative isolate min-h-[320px] overflow-hidden rounded-card bg-primary-800 shadow-[0_30px_80px_-28px_rgba(4,15,40,0.45)] sm:min-h-[330px] lg:min-h-[350px]"
        >
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
            <Image
              src="/cta-glow-bg.png"
              alt=""
              fill
              sizes="1200px"
              className="object-cover"
              priority
            />
          </div>
          {/* Left to right gradient keeps the headline and CTA legible while
              letting the glow artwork show through on the right, where the
              phone mockup sits on sm+. */}
          <div
            className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-primary-900/85 via-primary-900/55 to-primary-900/10"
            aria-hidden="true"
          />

          <div className="relative z-10 flex flex-col px-8 pt-10 sm:px-12 sm:pb-12 sm:pt-12 lg:max-w-[62%] lg:px-16 lg:py-14">
            <motion.h2
              id="cta-heading"
              variants={fadeUp(reduce)}
              className="text-[clamp(1.8rem,3.6vw,2.6rem)] font-semibold leading-[1.08] tracking-[-0.02em] text-white"
            >
              Start with AED 10.
              <br className="hidden sm:block" /> Grow it at your own pace.
            </motion.h2>

            <motion.p
              variants={fadeUp(reduce)}
              className="mt-4 max-w-[480px] text-base leading-[1.55] text-primary-100"
            >
              No minimum commitment, no lock-in, no monthly fee. Buy a
              little when you can, sell whenever you need to, and take it
              home as a coin or bar whenever you want.
            </motion.p>

            <motion.div variants={fadeUp(reduce)} className="mt-7">
              <DownloadAppLink
                className={cn(
                  "group inline-flex items-center gap-2 rounded-lg bg-white px-6 py-3",
                  "text-sm font-semibold text-primary-900 transition-all duration-200",
                  "hover:-translate-y-0.5 hover:bg-accent-100",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                )}
              >
                <Download
                  className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5"
                  strokeWidth={2.2}
                />
                Download App
              </DownloadAppLink>
            </motion.div>

            <motion.div
              variants={fadeUp(reduce)}
              className="mt-8 flex items-center gap-3"
            >
              <div className="flex -space-x-2.5">
                {avatars.map((a) => (
                  <span
                    key={a.name}
                    className="relative h-8 w-8 overflow-hidden rounded-full border-2 border-primary-800 bg-primary-100"
                  >
                    <Image
                      src={a.src}
                      alt={`${a.name} investor avatar`}
                      fill
                      sizes="32px"
                      className="object-cover"
                    />
                  </span>
                ))}
              </div>
              <p className="text-sm leading-snug text-primary-300">
                Trusted by <span className="font-semibold text-white">50,000+</span>{" "}
                investors across the UAE
              </p>
            </motion.div>

            {/* Phone — mobile only, sits last in the stacked content instead
                of poking above the card like it does on sm+. */}
            <motion.div
              variants={fadeUp(reduce)}
              className="relative mx-auto mt-8 aspect-[1328/1636] w-[140px] sm:hidden"
            >
              <div className="relative h-full w-full overflow-hidden rounded-t-[22px]">
                <Image
                  src="/bento-phone.png"
                  alt="JustGold app home screen showing a gold balance, buy and sell actions, and a price chart"
                  fill
                  sizes="140px"
                  className="object-cover object-top drop-shadow-[0_20px_40px_rgba(0,0,0,0.35)]"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
